import { clientLogger } from '@/lib/clientLogger';
/**
 * Capacitor Platform Utilities
 * 
 * This module provides cross-platform utilities that work on:
 * - Native Android/iOS (Capacitor)
 * - Mobile browsers
 * - Desktop browsers
 * 
 * Each utility automatically detects the platform and uses the appropriate implementation.
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { Clipboard } from '@capacitor/clipboard';
import { Dialog } from '@capacitor/dialog';
import { pushAppNotification, type NotificationType } from '@/components/ui/NotificationSystem';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App } from '@capacitor/app';
import { Share } from '@capacitor/share';

// ============================================================================
// Platform Detection
// ============================================================================

/**
 * Check if running as a native app (Android/iOS via Capacitor)
 */
export const isNative = (): boolean => {
    return Capacitor.isNativePlatform();
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
    return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => {
    return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running in a web browser
 */
export const isWeb = (): boolean => {
    return Capacitor.getPlatform() === 'web';
};

/**
 * Get the current platform
 */
export const getPlatform = (): 'android' | 'ios' | 'web' => {
    return Capacitor.getPlatform() as 'android' | 'ios' | 'web';
};

// ============================================================================
// Permission Request Serialization
// ============================================================================

// Android's Activity allows only ONE runtime permission request in flight at a
// time — overlapping requests are silently dropped with the logcat warning
// "Can request only one set of permissions at a time". At login several flows
// request permissions concurrently (location tracking, push notifications, local
// notifications), so the prompts race and some never appear. Route every OS
// permission request through this queue so they run strictly one after another.
let permissionLock: Promise<unknown> = Promise.resolve();

export const requestPermissionExclusive = <T>(fn: () => Promise<T>): Promise<T> => {
    const run = permissionLock.then(fn, fn);
    permissionLock = run.catch(() => {});
    return run;
};

// ============================================================================
// App Origin
// ============================================================================

/**
 * Get the public-facing app origin.
 * On native (Capacitor), window.location.origin is https://localhost which is
 * meaningless to external services and users. This helper returns the real
 * public domain from the VITE_APP_PUBLIC_URL env variable on native, and
 * falls back to window.location.origin on web.
 */
export const getAppOrigin = (): string => {
    if (isNative()) {
        return import.meta.env.VITE_APP_PUBLIC_URL;
    }
    return window.location.origin;
};

// ============================================================================
// Browser/URL Opening
// ============================================================================

/**
 * Open a URL in the system browser or a new tab
 * Uses Capacitor Browser plugin on native, window.open on web
 */
export const openUrl = async (url: string): Promise<void> => {
    if (isNative()) {
        await Browser.open({ url });
    } else {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

/**
 * Open the device's maps app with directions/search for an address.
 */
export const openNavigation = async (address: string): Promise<void> => {
    const encodedAddress = encodeURIComponent(address);

    // geo: is Android-only. iOS has no handler for it, so UIApplication.open fails
    // silently and the tap does nothing — iOS needs the maps: scheme instead.
    if (isNative() && isAndroid()) {
        window.location.href = `geo:0,0?q=${encodedAddress}`;
    } else if (isNative() && isIOS()) {
        window.location.href = `maps://?q=${encodedAddress}`;
    } else {
        // Web (incl. mobile browsers) - https URL deep-links to the maps app if installed
        await openUrl(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`);
    }
};

/**
 * Open a URL for downloading (PDFs, files, etc.)
 * On native, uses browser. On web, uses window.open.
 */
export const openDownloadUrl = async (url: string): Promise<void> => {
    if (isNative()) {
        // On native, open in system browser which handles downloads
        await Browser.open({ url });
    } else {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

// ============================================================================
// Persistent Storage (Preferences)
// ============================================================================

/**
 * Store a value persistently
 * Uses Capacitor Preferences on native, localStorage on web
 */
export const setStorageItem = async (key: string, value: string): Promise<void> => {
    if (isNative()) {
        await Preferences.set({ key, value });
    } else {
        localStorage.setItem(key, value);
    }
};

/**
 * Retrieve a value from persistent storage
 * Uses Capacitor Preferences on native, localStorage on web
 */
export const getStorageItem = async (key: string): Promise<string | null> => {
    if (isNative()) {
        const { value } = await Preferences.get({ key });
        return value;
    } else {
        return localStorage.getItem(key);
    }
};

/**
 * Remove a value from persistent storage
 * Uses Capacitor Preferences on native, localStorage on web
 */
/**
 * Which organization the app opens into. Defined here rather than in useGlobalOrganization so the
 * public signup page can clear it without importing the whole provider — that module pulls in
 * react-query, the API client and location tracking, none of which belong on a logged-out route.
 */
export const SELECTED_ORG_STORAGE_KEY = 'selected_org_id';

export const removeStorageItem = async (key: string): Promise<void> => {
    if (isNative()) {
        await Preferences.remove({ key });
    } else {
        localStorage.removeItem(key);
    }
};

// ============================================================================
// Clipboard
// ============================================================================

/**
 * Copy text to clipboard
 * Uses Capacitor Clipboard on native, navigator.clipboard on web with fallback
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        if (isNative()) {
            await Clipboard.write({ string: text });
            return true;
        } else {
            // Web fallback with multiple approaches
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            } else {
                // Fallback for older browsers or non-HTTPS
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                textArea.style.top = '-9999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    return true;
                } finally {
                    document.body.removeChild(textArea);
                }
            }
        }
    } catch (error) {
        clientLogger.error('Failed to copy to clipboard:', error);
        return false;
    }
};

const isShareCancel = (error: any): boolean =>
    error?.name === 'AbortError' || /cancel|abort|dismiss/i.test(error?.message ?? '');

/**
 * Open the OS share sheet (WhatsApp, SMS, Gmail, etc.).
 * Degrades in order: Capacitor Share plugin (native builds) → Web Share API (HTTPS browsers &
 * the iOS WebView) → 'unavailable' so the caller can copy instead. User-dismissed sheets
 * resolve as 'cancelled'. The plugin step is skipped/falls through when the native build
 * doesn't bundle @capacitor/share yet (e.g. JS shipped via OTA before an app rebuild).
 */
export const nativeShare = async (
    opts: { title?: string; text?: string; url?: string }
): Promise<'shared' | 'cancelled' | 'unavailable'> => {
    // Some Web Share implementations reject when passed undefined keys — send only what we have.
    const data = {
        ...(opts.title ? { title: opts.title } : {}),
        ...(opts.text ? { text: opts.text } : {}),
        ...(opts.url ? { url: opts.url } : {}),
    };

    if (isNative()) {
        try {
            await Share.share({ ...data, dialogTitle: opts.title });
            return 'shared';
        } catch (error: any) {
            if (isShareCancel(error)) return 'cancelled';
            // Plugin not present in this build — fall through to the Web Share API.
            clientLogger.warn('Capacitor Share unavailable, trying Web Share:', error);
        }
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
            await navigator.share(data);
            return 'shared';
        } catch (error: any) {
            if (isShareCancel(error)) return 'cancelled';
            clientLogger.error('Web share failed:', error);
        }
    }

    return 'unavailable';
};

/**
 * Read text from clipboard
 * Uses Capacitor Clipboard on native, navigator.clipboard on web
 */
export const readFromClipboard = async (): Promise<string | null> => {
    try {
        if (isNative()) {
            const { value } = await Clipboard.read();
            return value;
        } else {
            if (navigator.clipboard && navigator.clipboard.readText) {
                return await navigator.clipboard.readText();
            }
            return null;
        }
    } catch (error) {
        clientLogger.error('Failed to read from clipboard:', error);
        return null;
    }
};

// ============================================================================
// Dialogs
// ============================================================================

function inferAlertType(message: string, title?: string): NotificationType {
    const blob = `${title ?? ''} ${message}`.toLowerCase();
    if (/\b(fail|failed|error|unable|could not)\b/.test(blob)) return 'error';
    if (/\b(success|successfully|copied|deleted|settled)\b/.test(blob)) return 'success';
    return 'error';
}

/**
 * Show an in-app toast (same notification strip used elsewhere in the app).
 * Replaces Capacitor Dialog / browser `alert` so the UI never shows a Chrome or OS dialog.
 */
export const showAlert = async (message: string, title?: string): Promise<void> => {
    pushAppNotification({
        type: inferAlertType(message, title),
        title: title || message,
        message: title ? message : undefined,
    });
};

/**
 * Show a confirm dialog
 * Uses Capacitor Dialog on native, browser confirm on web
 * Returns true if confirmed, false if cancelled
 */
export const showConfirm = async (
    message: string,
    title?: string,
    okButtonTitle?: string,
    cancelButtonTitle?: string
): Promise<boolean> => {
    if (isNative()) {
        const { value } = await Dialog.confirm({
            title: title || 'Confirm',
            message,
            okButtonTitle: okButtonTitle || 'OK',
            cancelButtonTitle: cancelButtonTitle || 'Cancel',
        });
        return value;
    } else {
        // Use native browser confirm for web
        return confirm(title ? `${title}\n\n${message}` : message);
    }
};

/**
 * Show a prompt dialog
 * Uses Capacitor Dialog on native, browser prompt on web
 * Returns the entered value or null if cancelled
 */
export const showPrompt = async (
    message: string,
    title?: string,
    defaultValue?: string
): Promise<string | null> => {
    if (isNative()) {
        const { value, cancelled } = await Dialog.prompt({
            title: title || 'Input',
            message,
            inputText: defaultValue,
        });
        return cancelled ? null : value;
    } else {
        return prompt(title ? `${title}\n\n${message}` : message, defaultValue);
    }
};

// ============================================================================
// Status Bar (Native Only)
// ============================================================================

/**
 * Configure the status bar (native only)
 * No-op on web
 */
export const configureStatusBar = async (options: {
    style?: 'dark' | 'light';
    backgroundColor?: string;
    overlaysWebView?: boolean;
}): Promise<void> => {
    if (!isNative()) return;

    try {
        if (options.style) {
            await StatusBar.setStyle({
                style: options.style === 'dark' ? Style.Dark : Style.Light,
            });
        }

        if (options.backgroundColor && isAndroid()) {
            await StatusBar.setBackgroundColor({ color: options.backgroundColor });
        }

        if (options.overlaysWebView !== undefined) {
            await StatusBar.setOverlaysWebView({ overlay: options.overlaysWebView });
        }
    } catch (error) {
        clientLogger.error('Failed to configure status bar:', error);
    }
};

/**
 * Show the status bar (native only)
 */
export const showStatusBar = async (): Promise<void> => {
    if (isNative()) {
        try {
            await StatusBar.show();
        } catch (error) {
            clientLogger.error('Failed to show status bar:', error);
        }
    }
};

/**
 * Hide the status bar (native only)
 */
export const hideStatusBar = async (): Promise<void> => {
    if (isNative()) {
        try {
            await StatusBar.hide();
        } catch (error) {
            clientLogger.error('Failed to hide status bar:', error);
        }
    }
};

// ============================================================================
// App Back Button (Native Only)
// ============================================================================

type BackButtonCallback = (canGoBack: boolean) => void;
let backButtonListeners: BackButtonCallback[] = [];

/**
 * Register a back button handler (native only)
 * Returns a function to unregister the handler
 */
export const registerBackButtonHandler = (callback: BackButtonCallback): (() => void) => {
    if (!isNative()) {
        // On web, we use popstate events which are already handled via window.history
        return () => { };
    }

    backButtonListeners.push(callback);

    // Return unregister function
    return () => {
        backButtonListeners = backButtonListeners.filter(cb => cb !== callback);
    };
};

// Initialize back button listener for native
if (typeof window !== 'undefined') {
    // Defer initialization to allow Capacitor to fully load
    setTimeout(() => {
        if (isNative()) {
            App.addListener('backButton', ({ canGoBack }) => {
                // Call the most recently added listener (top of stack)
                if (backButtonListeners.length > 0) {
                    const lastListener = backButtonListeners[backButtonListeners.length - 1];
                    lastListener(canGoBack);
                } else if (canGoBack) {
                    // Default behavior: go back in history
                    window.history.back();
                } else {
                    // At root, minimize app (Android only)
                    App.minimizeApp();
                }
            });
        }
    }, 100);
}

// ============================================================================
// App Lifecycle
// ============================================================================

/**
 * Exit the app (native only, no-op on web)
 */
export const exitApp = (): void => {
    if (isNative()) {
        App.exitApp();
    }
};

/**
 * Minimize the app (Android only, no-op on iOS and web)
 */
export const minimizeApp = (): void => {
    if (isAndroid()) {
        App.minimizeApp();
    }
};

// ============================================================================
// Safe Area Detection for Android
// ============================================================================

/**
 * Detect and apply safe area insets for Android
 * Android WebView doesn't always support env(safe-area-inset-*) properly
 * This function calculates the insets and sets CSS custom properties
 */
const applyAndroidSafeAreas = (): void => {
    if (!isAndroid()) return;
 
    // Calculate status bar height (top inset)
    // On Android, we can estimate this from screen vs viewport difference
    const screenHeight = window.screen.height;
    const innerHeight = window.innerHeight;
    
    // MainActivity writes the measured nav bar height into --android-safe-area-bottom. This guess is
    // only for older APKs that don't measure it, so never overwrite a value native already set.
    const rootStyle = document.documentElement.style;
    if (!rootStyle.getPropertyValue('--android-safe-area-bottom')) {
        rootStyle.setProperty('--android-safe-area-bottom', '10px');
    }

    rootStyle.setProperty('--android-safe-area-top', '0px');

    clientLogger.info('[Capacitor] Applied Android safe areas:', {
        top: '0px',
        bottom: rootStyle.getPropertyValue('--android-safe-area-bottom'),
        screenHeight,
        innerHeight
    });
};

// ============================================================================
// Initialize Platform-Specific Settings
// ============================================================================

/**
 * Initialize platform-specific settings
 * Call this once when the app starts
 */
export const initializePlatform = async (): Promise<void> => {
    if (isNative()) {
        // Simple status bar setup:
        // - Transparent background so content shows through
        // - Dark icons (works on light backgrounds)
        // - Overlays WebView so app can draw behind status bar
        //
        // Note the inversion: Capacitor's Style.Dark means "dark UI", i.e. *light* status-bar
        // content — the opposite of what the "dark icons" intent above reads like. Passing
        // 'dark' here therefore yields white icons, which vanish against this app's near-white
        // backgrounds while overlaysWebView draws content underneath them.
        //
        // Only corrected for iOS. Android has shipped with the current value, OEM status bars
        // vary, and some auto-contrast regardless — so flip it there too only after eyeballing
        // a real device rather than as an unverified change to a live platform.
        await configureStatusBar({
            style: isIOS() ? 'light' : 'dark',
            backgroundColor: '#00000000', // transparent
            overlaysWebView: true,
        });
        
        // Apply Android safe areas via JavaScript fallback
        if (isAndroid()) {
            // Wait for DOM to be ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', applyAndroidSafeAreas);
            } else {
                applyAndroidSafeAreas();
            }
            
            // Re-apply on orientation change
            window.addEventListener('orientationchange', () => {
                setTimeout(applyAndroidSafeAreas, 100);
            });
            
            // Re-apply on resize (soft keyboard, etc.)
            window.addEventListener('resize', () => {
                setTimeout(applyAndroidSafeAreas, 100);
            });
        }
    }
};
